/**
 * Module: sbpErrors
 * Role: Map backend SBP validation error codes to user-facing RU text
 *       and route field errors to the correct wizard step.
 * Uses: nothing (pure helper)
 * Used by: RequisitesScreen.tsx
 * Glossary: ai/glossary/buyer.md
 *
 * BUG-1231: backend returns per-field 422 errors with machine codes
 * (src/shared/validation.py, src/api/v2/buyer/profile.py).
 * This module maps code→text and classifies field→step.
 */

// ─── Error code → RU text ────────────────────────────────────────────────────

const ERROR_MESSAGES: Record<string, string> = {
  // phone (src/shared/validation.py validate_phone)
  phone_too_short: 'Номер слишком короткий',
  invalid_country_code: 'Начни с +7',
  invalid_phone_length: 'Нужно +7 и 10 цифр',
  // fio (src/shared/validation.py validate_fio)
  fio_empty: 'Укажи ФИО',
  fio_too_short: 'Нужны фамилия и имя',
  fio_too_long: 'Слишком много слов — Фамилия Имя Отчество',
  fio_part_too_short: 'Слишком короткое слово в ФИО',
  fio_part_too_long: 'Слишком длинное слово в ФИО',
  fio_non_cyrillic: 'Только кириллица (можно дефис)',
  // bank (src/api/v2/buyer/profile.py _validate_sbp_bank)
  invalid_sbp_bank: 'Выбери банк из списка',
};

const FALLBACK_MESSAGE = 'Проверь данные и попробуй ещё раз';

export function getErrorMessage(code: string): string {
  return ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE;
}

// ─── Field → Step classification ─────────────────────────────────────────────

type Step = 'bank' | 'phone' | 'fio';

const FIELD_TO_STEP: Record<string, Step> = {
  sbp_phone: 'phone',
  sbp_bank: 'bank',
  fio_last_name: 'fio',
  fio_first_name: 'fio',
  fio_patronymic: 'fio',
};

// Step priority: earlier steps come first so we navigate to the earliest broken one.
const STEP_ORDER: Record<Step, number> = { bank: 0, phone: 1, fio: 2 };

export interface FieldError {
  field: string;
  code: string;
}

export interface RoutedErrors {
  /** The earliest broken step to navigate to */
  targetStep: Step;
  /** Per-step error texts: errors.phone, errors.fio, errors.bank */
  errors: Partial<Record<Step, string>>;
}

/**
 * Classify field errors into wizard steps and pick the earliest broken step.
 * Multiple errors on the same step are joined with "; ".
 */
export function routeFieldErrors(fieldErrors: FieldError[]): RoutedErrors | null {
  if (fieldErrors.length === 0) return null;

  const errors: Partial<Record<Step, string>> = {};
  let targetStep: Step = 'fio'; // default to last step
  let targetOrder = STEP_ORDER[targetStep];

  for (const { field, code } of fieldErrors) {
    const step = FIELD_TO_STEP[field] ?? 'fio'; // unknown field → show on fio (current step)
    const msg = getErrorMessage(code);
    errors[step] = errors[step] ? `${errors[step]}; ${msg}` : msg;

    const order = STEP_ORDER[step];
    if (order < targetOrder) {
      targetStep = step;
      targetOrder = order;
    }
  }

  return { targetStep, errors };
}

// ─── Exports ─────────────────────────────────────────────────────────────────
// getErrorMessage, routeFieldErrors, FieldError, RoutedErrors — 4 exports
